import calculateBoundingBox from '@turf/bbox';
import calculateBearing from '@turf/bearing';
import * as bootstrap from 'bootstrap';
import { Feature, FeatureCollection, Point } from 'geojson';
import mapboxgl from 'mapbox-gl';
import medalFirstPlaceIcon from '../../images/icons/medal-place-1.svg';
import medalSecondPlaceIcon from '../../images/icons/medal-place-2.svg';
import medalThirdPlaceIcon from '../../images/icons/medal-place-3.svg';
import { addAchievementMarkersFromCollection } from '../../js/map/achievement_markers';
import GeojsonMapController from '../../js/map/geojson_map_controller';
import { journeyPathLayers } from '../../js/map/journey_path_layers';
import type { MapLayerSpecification } from '../../js/map/map_layer_specification';

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
mapboxgl.accessToken = process.env.MAPBOX_ACCESS_TOKEN!;

type CompetitorFeature = Feature<
  Point,
  {
    distanceValue: string;
    distanceUnitLabel: string;
    position: number;
    name: string;
    thumbnail: string;
  }
>;

export default class MapController extends GeojsonMapController {
  public static readonly values = {
    ...GeojsonMapController.values,
    config: Object,
    geoJsonUrl: String
  };

  protected _mapOptions(): Partial<mapboxgl.MapboxOptions> {
    return {
      center: [170.5, -45],
      pitch: 60,
      clickTolerance: 10,
      bearing: 10,
      zoom: 6.5,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      minZoom: 6
    };
  }

  public addTerrain(): void {
    this.map.addSource('mapbox-dem', {
      type: 'raster-dem',
      url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      tileSize: 512,
      maxzoom: 14
    });
    this.map.setTerrain({ source: 'mapbox-dem' });
    this.map.getStyle().layers.forEach(({ type, id }) => {
      if (!['raster', 'satellite'].includes(type)) {
        this.map.removeLayer(id);
      }
    });
  }

  protected _beforeSetupMap(): void {
    this.addTerrain();
  }

  protected _lineLayers(): MapLayerSpecification[] {
    const journeyLayers: MapLayerSpecification[] = journeyPathLayers({
      lineWidth: 4,
      remainingOpacity: 0.75,
      remainingDash: [0.1, 2.5]
    });

    const progressPathLayer: MapLayerSpecification = {
      id: 'progress-update-path',
      source: 'map-data',
      type: 'line',
      filter: ['==', ['get', 'variant'], 'progress-update-path'],
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#FD3060',
        'line-width': 6
      }
    };

    return journeyLayers.concat(progressPathLayer);
  }

  protected _handlePointFeatures(mapData: FeatureCollection): void {
    addAchievementMarkersFromCollection(
      this.map,
      mapData.features,
      this.markers
    );

    for (const competitorFeature of mapData.features.filter(
      feature =>
        feature.properties?.variant === 'competitor' &&
        feature.geometry.type === 'Point'
    ) as CompetitorFeature[]) {
      this.addCompetitorMarker(competitorFeature);
    }

    for (const competitorFeature of mapData.features.filter(
      feature =>
        feature.properties?.variant === 'current-competitor' &&
        feature.geometry.type === 'Point'
    ) as CompetitorFeature[]) {
      this.addCompetitorMarker(competitorFeature, true);
    }
  }

  public addCompetitorMarker(
    feature: CompetitorFeature,
    isCurrent = false
  ): void {
    const el = document.createElement('div');
    const innerEl = document.createElement('div');
    const wrapperEl = document.createElement('div');
    const iconHeaderEl = document.createElement('div');
    const iconEl = document.createElement('img');
    const achievementThumbnail = document.createElement('img');

    el.className = 'competitor-marker';
    iconHeaderEl.className = 'competitor-marker__title';
    iconEl.width = 20;
    iconEl.height = 20;

    if (isCurrent) {
      el.classList.add('current');
    }

    if (feature.properties.position === 1) {
      iconEl.src = medalFirstPlaceIcon;
      iconHeaderEl.append(iconEl);
      innerEl.appendChild(iconHeaderEl);
    } else if (feature.properties.position === 2) {
      iconEl.src = medalSecondPlaceIcon;
      iconHeaderEl.append(iconEl);
      innerEl.appendChild(iconHeaderEl);
    } else if (feature.properties.position === 3) {
      iconEl.src = medalThirdPlaceIcon;
      iconHeaderEl.append(iconEl);
      innerEl.appendChild(iconHeaderEl);
    }

    achievementThumbnail.src = feature.properties.thumbnail;
    achievementThumbnail.className = 'competitor-marker__thumbnail';
    innerEl.className = 'competitor-marker__inner';

    innerEl.appendChild(achievementThumbnail);
    wrapperEl.appendChild(el);
    el.appendChild(innerEl);

    // eslint-disable-next-line no-new
    new bootstrap.Tooltip(el, {
      title: isCurrent
        ? `${feature.properties.name} (you)`
        : feature.properties.name,
      template: `
        <div class="tooltip" role="tooltip">
          <div class="tooltip-arrow border-secondary"></div>
          <div class="text-bg-secondary rounded-2 display-6 fw-normal tooltip-inner"></div>
        </div>`,
      placement: 'bottom'
    });

    new mapboxgl.Marker(wrapperEl)
      .setLngLat([
        feature.geometry.coordinates[0],
        feature.geometry.coordinates[1]
      ])
      .addTo(this.map);
  }

  protected _afterSetupMap(mapData: FeatureCollection): void {
    if (this.configValue.center) {
      this.zoomTo({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Point',
              coordinates: this.configValue.center as number[]
            }
          }
        ]
      });
    } else if (this.configValue.zoomToFeature) {
      this.zoomTo(this.configValue.zoomToFeature as GeoJSON.Feature);
    } else {
      this.zoomTo(mapData);
    }
  }

  public zoomTo(
    ...features: GeoJSON.Feature[] | GeoJSON.FeatureCollection[]
  ): void {
    const feature =
      features.length > 1
        ? ({ type: 'FeatureCollection', features } as GeoJSON.FeatureCollection)
        : features[0];
    const [x1, y1, x2, y2] = calculateBoundingBox(feature);
    const bearingAlongLine = calculateBearing([x1, y1], [x2, y2]);

    this.map.fitBounds([x1, y1, x2, y2], {
      bearing: bearingAlongLine + 20,
      pitch: (this.configValue.pitch as number | undefined) ?? 60,
      maxDuration: 500,
      padding: 20
    });
  }
}
